import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { howItWorksSteps } from '@/mocks/homeData';
import { Link } from 'react-router-dom';

export default function HowItWorks() {
  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      {/* Page Header */}
      <section className="w-full pt-28 md:pt-32 pb-10 md:pb-14 px-4 md:px-6 bg-background-100">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 text-xs font-medium text-primary-700 bg-primary-100 rounded-full">
            How It Works
          </span>
          <h1 className="mt-4 text-3xl md:text-5xl font-bold font-heading text-foreground-950 leading-tight">
            Four Steps to Cleaner Beaches
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-xl mx-auto leading-relaxed">
            The subscription model that turns $14/month into real, tangible beach cleanup action on your local coastline. Here is exactly how it works from signup to impact.
          </p>
        </div>
      </section>

      {/* Steps Overview */}
      <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {howItWorksSteps.map((step, index) => (
              <div
                key={step.id}
                className="relative p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/60 group hover:bg-primary-500 hover:border-primary-500 transition-all duration-300 cursor-default"
              >
                <div className="absolute top-3 right-3 text-4xl md:text-5xl font-bold font-heading text-foreground-100 group-hover:text-background-50/20 transition-colors">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div className="relative z-10">
                  <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-background-200 rounded-lg group-hover:bg-background-50/20 transition-colors mb-4">
                    <i className={`${step.icon} text-lg md:text-xl text-primary-600 group-hover:text-background-50 transition-colors`}></i>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-950 group-hover:text-background-50 transition-colors">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-xs md:text-sm text-foreground-600 group-hover:text-background-50/85 transition-colors leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detail Step 1 - Sign Up (clean beach) */}
      <section className="w-full bg-background-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-14 md:py-20 border-b border-background-200/60">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
            <div className="w-full lg:w-1/2">
              <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Person%20sitting%20on%20a%20beach%20towel%20with%20a%20laptop%20overlooking%20a%20beautiful%20clean%20sandy%20beach%20and%20turquoise%20ocean%2C%20warm%20sunlight%2C%20relaxed%20coastal%20vibe%2C%20clean%20minimalist%20lifestyle%20photography%2C%20soft%20natural%20lighting&width=800&height=600&seq=how-beach-signup&orientation=landscape"
                  alt="Sign up for beach cleanup donations"
                  className="w-full h-full object-cover object-top"
                  title="Sign Up & Subscribe to Clear Beaches Program"
                />
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <span className="text-xs font-medium text-accent-600">Step 1</span>
              <h2 className="mt-2 text-xl md:text-3xl font-bold font-heading text-foreground-950 leading-tight">
                Sign Up & Subscribe
              </h2>
              <p className="mt-3 text-sm md:text-base font-medium text-primary-600">
                Create your account and set up your $14 monthly donation in under two minutes.
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                Getting started is simple. Create your free account, tell us about your local coastal community, and set up your monthly $14 donation through our secure payment system. Your first donation triggers the shipment of your very own beach cleanup kit — waterproof gloves, mesh collection bags, sand sifters, grabbers, and safety supplies. Everything you need arrives at your doorstep within a week.
              </p>
            </div>
          </div>
        </div>

        {/* Detail Step 2 - Dirty Beach (showing need for help) */}
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-14 md:py-20 border-b border-background-200/60">
          <div className="flex flex-col lg:flex-row-reverse gap-8 md:gap-12 items-center">
            <div className="w-full lg:w-1/2">
              <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Sad%20scene%20of%20a%20beach%20covered%20in%20plastic%20waste%20and%20debris%2C%20scattered%20bottles%20and%20trash%20on%20sand%2C%20overcast%20sky%2C%20contrast%20between%20natural%20beauty%20and%20pollution%2C%20environmental%20documentary%20photography%2C%20urgent%20tone%2C%20showing%20why%20cleanup%20is%20needed&width=800&height=600&seq=how-beach-dirty-kit&orientation=landscape"
                  alt="Dirty beach covered in plastic waste needing cleanup"
                  className="w-full h-full object-cover object-top"
                  title="Beaches Need Your Help — Join the Cleanup Today"
                />
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <span className="text-xs font-medium text-accent-600">Step 2</span>
              <h2 className="mt-2 text-xl md:text-3xl font-bold font-heading text-foreground-950 leading-tight">
                Receive Your Kit & Take Action
              </h2>
              <p className="mt-3 text-sm md:text-base font-medium text-primary-600">
                Unbox your free beach cleanup kit and organize your first shoreline cleanup event.
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                Your kit includes beach-ready equipment — mesh bags that drain water while collecting debris, sand sifters for microplastic removal, waterproof gloves for wet conditions, and grabbers designed for shoreline terrain. Many subscribers tell us their first beach cleanup becomes a regular community event — fellow beachgoers start joining in month after month.
              </p>
            </div>
          </div>
        </div>

        {/* Detail Step 3 - Dirty Beach (another one showing need) */}
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-14 md:py-20 border-b border-background-200/60">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
            <div className="w-full lg:w-1/2">
              <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Polluted%20beach%20with%20plastic%20bottles%20fishing%20nets%20and%20microplastics%20mixed%20with%20sand%20and%20seaweed%2C%20grey%20overcast%20sky%2C%20waves%20carrying%20debris%20onto%20shore%2C%20stark%20environmental%20crisis%20documentary%20photography%2C%20showing%20urgent%20need%20for%20beach%20cleanup%20action&width=800&height=600&seq=how-beach-dirty-impact&orientation=landscape"
                  alt="Polluted shoreline with debris showing urgent need for cleanup"
                  className="w-full h-full object-cover object-top"
                  title="Coastal pollution — your donations make a difference"
                />
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <span className="text-xs font-medium text-accent-600">Step 3</span>
              <h2 className="mt-2 text-xl md:text-3xl font-bold font-heading text-foreground-950 leading-tight">
                Sustained Monthly Impact
              </h2>
              <p className="mt-3 text-sm md:text-base font-medium text-primary-600">
                Every other month of continued donation sends a new kit to a volunteer group on your coastline.
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                Here is where the magic happens. After your first donation, every other month of continued giving funds a brand new cleanup kit that goes directly to a volunteer group in your local coastal area. We work with surf clubs, marine conservation groups, schools, and neighborhood associations to identify active groups that need supplies. Track every kit on your dashboard — see exactly which beach it went to and the impact it created.
              </p>
            </div>
          </div>
        </div>

        {/* Detail Step 4 - Clean Beach (transformation result) */}
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="flex flex-col lg:flex-row-reverse gap-8 md:gap-12 items-center">
            <div className="w-full lg:w-1/2">
              <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Wide%20aerial%20view%20of%20a%20pristine%20clean%20sandy%20beach%20with%20crystal%20clear%20turquoise%20water%2C%20happy%20families%20enjoying%20the%20shoreline%2C%20bright%20sunny%20day%2C%20lush%20coastal%20vegetation%2C%20paradise%20aesthetic%2C%20uplifting%20atmospheric%20travel%20photography%2C%20warm%20golden%20tones&width=800&height=600&seq=how-beach-clean-result&orientation=landscape"
                  alt="Pristine clean beach with families enjoying the shoreline"
                  className="w-full h-full object-cover object-top"
                  title="The result — clean beaches through sustained volunteer action"
                />
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <span className="text-xs font-medium text-accent-600">Step 4</span>
              <h2 className="mt-2 text-xl md:text-3xl font-bold font-heading text-foreground-950 leading-tight">
                Watch Beaches Transform
              </h2>
              <p className="mt-3 text-sm md:text-base font-medium text-primary-600">
                See your impact multiply as more subscribers join and more coastlines get cleaned.
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                As our subscriber base grows, so does the network of equipped volunteer groups along the coast. Some beach communities now have multiple groups operating on different schedules — one tackling the main beach, another the rocky coves, a third handling the dunes. The subscription model means consistent, predictable funding that volunteer groups can rely on. No more scrambling for supplies or buying things out of pocket.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50 text-center">
        <h3 className="text-xl md:text-3xl font-bold font-heading text-foreground-950">
          Ready to start your beach cleanup journey?
        </h3>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-md mx-auto">
          Join thousands of subscribers making their coastlines cleaner, one kit at a time.
        </p>
        <Link
          to="/donate"
          className="mt-6 inline-block px-8 py-3 md:px-10 md:py-3.5 text-sm md:text-base font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          Start Your $14/month Donation
        </Link>
      </section>

      <Footer />
    </main>
  );
}